import PartnerDTO from 'models/dto/partnerDTO';
import UserDTO from 'models/dto/userDTO';
import { UserReadConfirmationDTO } from 'models/dto/userReadConfirmationDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import { IdentityProviderType } from 'models/identityProvider';
import Partner from 'models/partner';
import User from 'models/user';
import { UserReadConfirmation } from 'models/userReadConfirmation';
import { fromApiDateTimeOptional, toApiDateTimeOptional, toApiDateTimeRequired } from 'utils/datetime';
import { newGuidNil } from 'utils/guid';
import { mapFromResourceLink } from './linkMapping';

export function mapFromUsers(userDTOs: UserDTO[], cache: GlobalDataCache | undefined): User[] {
  const userList: User[] = [];

  for (const userDTO of userDTOs) {
    const newUser = mapFromUser(userDTO, cache);
    if (newUser) userList.push(newUser);
  }

  return userList;
}

export function mapFromUser(userDTO: UserDTO | undefined, cache: GlobalDataCache | undefined): User | undefined {
  if (userDTO) {
    const newUser = new User(userDTO.userId, userDTO.email, userDTO.name);
    newUser.tenantId = userDTO.tenantId;
    newUser.identityProviderType = userDTO.identityProviderType ?? IdentityProviderType.Entra;
    newUser.providerUserId = userDTO.providerUserId;
    newUser.userType = userDTO.userType;
    newUser.created = fromApiDateTimeOptional(userDTO.created);
    newUser.hasLicense = userDTO.hasLicense;
    newUser.lastLogin = fromApiDateTimeOptional(userDTO.lastLogin);
    newUser.roles = cache ? userDTO.roles?.map((id) => cache.roles.get(id)) : [];
    newUser.groups = userDTO.groups;
    newUser.userTimeZoneCode = userDTO.userTimeZoneCode;
    newUser.userLanguageCode = userDTO.userLanguageCode;
    newUser.systemRoleIds =
      userDTO.systemRoleIds && userDTO.systemRoleIds.length > 0
        ? userDTO.systemRoleIds
        : newUser.roles?.map((r) => r.systemRoleId as number) || [];
    newUser.eventSubscriptionId = userDTO.eventSubscriptionId;
    newUser.eventSubscriptionExp = userDTO.eventSubscriptionExp;
    newUser.eventSubscriptionSync = userDTO.eventSubscriptionSync;

    return newUser;
  }

  return undefined;
}

export function mapToUser(user: User): UserDTO {
  const userDTO = new UserDTO();
  //a user invited by e-mail has no id yet: the empty Guid tells the API to assign a placeholder identity
  userDTO.userId = user.id || newGuidNil();
  userDTO.identityProviderType = user.identityProviderType;
  userDTO.providerUserId = user.providerUserId;
  userDTO.name = user.name;
  userDTO.email = user.email;
  userDTO.userType = user.userType;
  userDTO.hasLicense = user.hasLicense;
  userDTO.userTimeZoneCode = user.userTimeZoneCode;
  userDTO.userLanguageCode = user.userLanguageCode;

  return userDTO;
}

//
// Partner mapping
//
export function mapFromPartners(partnerDTOs: PartnerDTO[]): Partner[] {
  const partnerList: Partner[] = [];

  for (const partnerDTO of partnerDTOs) {
    const newPartner = mapFromPartner(partnerDTO);
    partnerList.push(newPartner);
  }

  return partnerList;
}

export function mapFromPartner(partnerDTO: PartnerDTO): Partner {
  const newPartner = new Partner();
  newPartner.tenantId = partnerDTO.tenantId;
  newPartner.tenantName = partnerDTO.tenantName;
  newPartner.userId = partnerDTO.userId;
  newPartner.name = partnerDTO.name;
  newPartner.email = partnerDTO.email;

  return newPartner;
}

//
// UserReadConfirmation mapping
//
export function mapFromUserReadConfirmations(
  userReadConfirmationDTOs: UserReadConfirmationDTO[] | undefined,
  cache: GlobalDataCache,
): UserReadConfirmation[] {
  if (!userReadConfirmationDTOs) {
    return [];
  }
  const userReadConfirmationList: UserReadConfirmation[] = [];
  for (const userReadConfirmationDTO of userReadConfirmationDTOs) {
    const newUserReadConfirmation = mapFromUserReadConfirmation(userReadConfirmationDTO, cache);
    userReadConfirmationList.push(newUserReadConfirmation);
  }

  return userReadConfirmationList;
}

export function mapFromUserReadConfirmation(
  userReadConfirmationDTO: UserReadConfirmationDTO,
  cache: GlobalDataCache,
): UserReadConfirmation {
  const newUserReadConfirmation = new UserReadConfirmation();
  newUserReadConfirmation.userId = userReadConfirmationDTO.userId ?? '';
  newUserReadConfirmation.linkId = userReadConfirmationDTO.linkId ?? 0;
  newUserReadConfirmation.version = userReadConfirmationDTO.version ?? '';
  newUserReadConfirmation.created = fromApiDateTimeOptional(userReadConfirmationDTO.created) ?? new Date();
  newUserReadConfirmation.readConfirmed = fromApiDateTimeOptional(userReadConfirmationDTO.readConfirmed);
  newUserReadConfirmation.lastReminder = fromApiDateTimeOptional(userReadConfirmationDTO.lastReminder);
  newUserReadConfirmation.link = userReadConfirmationDTO.link
    ? mapFromResourceLink(userReadConfirmationDTO.link, cache)
    : undefined;
  newUserReadConfirmation.requestedVersion = userReadConfirmationDTO.requestedVersion ?? '';
  newUserReadConfirmation.audienceIds = userReadConfirmationDTO.audienceIds?.idList?.map((id) => Number.parseInt(id));

  return newUserReadConfirmation;
}

export function mapToUserReadConfirmations(userReadConfirmations: UserReadConfirmation[]): UserReadConfirmationDTO[] {
  const userReadConfirmationDTOs: UserReadConfirmationDTO[] = [];
  for (const userReadConfirmation of userReadConfirmations) {
    const userReadConfirmationDTO = mapToUserReadConfirmation(userReadConfirmation);
    userReadConfirmationDTOs.push(userReadConfirmationDTO);
  }

  return userReadConfirmationDTOs;
}

export function mapToUserReadConfirmation(userReadConfirmation: UserReadConfirmation): UserReadConfirmationDTO {
  const userReadConfirmationDTO = new UserReadConfirmationDTO();
  userReadConfirmationDTO.userId = userReadConfirmation.userId;
  userReadConfirmationDTO.linkId = userReadConfirmation.linkId;
  userReadConfirmationDTO.version = userReadConfirmation.version;
  userReadConfirmationDTO.created = toApiDateTimeRequired(userReadConfirmation.created);
  userReadConfirmationDTO.readConfirmed = toApiDateTimeOptional(userReadConfirmation.readConfirmed);
  userReadConfirmationDTO.lastReminder = toApiDateTimeOptional(userReadConfirmation.lastReminder);

  return userReadConfirmationDTO;
}
